import { DependencyList, useEffect, useRef, useState } from "react";
import { useHandler } from "./hook";
import { Loader, LoadState } from "./load";

export interface UsePollOptions {
  /**
   * If true, the first request is made immediately.
   * If false, the first request is delayed by the interval.
   * @default true
   */
  immediate?: boolean;
}

/**
 * Polls a loader function at a specified interval.
 *
 * - The next poll only starts after the previous request completes (success or error)
 * - If a request is still in flight when the interval fires, the poll is skipped
 * - The interval timer resets after each completed request
 */
export function usePoll<T>(
  fn: Loader<T>,
  interval: Temporal.Duration,
  deps: DependencyList,
  options: UsePollOptions = {},
): LoadState<T> {
  const { immediate = true } = options;

  const [state, setState] = useState<LoadState<T>>({ pending: false });
  const intervalMs = interval.total("milliseconds");

  const inFlightRef = useRef(false);
  const isFirstRequestRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const scheduleNext = useHandler(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(executeRequest, intervalMs);
  });

  const executeRequest = useHandler(() => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    const isFirst = isFirstRequestRef.current;
    isFirstRequestRef.current = false;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (isFirst) {
      setState((state) => ({ ...state, pending: true }));
    }

    fn(abortController.signal).then(
      (value) => {
        if (abortController.signal.aborted) {
          return;
        }
        inFlightRef.current = false;
        setState({ pending: false, value });
        scheduleNext();
      },
      (error) => {
        if (abortController.signal.aborted) {
          return;
        }
        inFlightRef.current = false;
        setState({ pending: false, error });
        scheduleNext();
      },
    );
  });

  useEffect(() => {
    if (immediate) {
      executeRequest();
    } else {
      scheduleNext();
    }

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      inFlightRef.current = false;
      isFirstRequestRef.current = true;
      setState((state) => ({ ...state, pending: false }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, executeRequest, scheduleNext, ...deps]);

  return state;
}
